import type { SupportEvent } from '../components/support/SupportAlerts';
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Socket } from 'socket.io-client';
import { createSignalingSocket, type PresenceUpdate, type SessionChanged } from '../lib/socket';
import { tokenStore } from '../lib/token-store';
import { qk } from '../api/keys';

export interface SignalingState {
  connected: boolean;
  supportEvent: SupportEvent | null;
  /** Users seen going online/offline since this socket connected. */
  presence: Record<string, boolean>;
  lastSessionChange: SessionChanged | null;
}

/**
 * Keeps one socket for the whole app.
 *
 * The dashboard is a monitoring surface: a tab can sit open for an hour, so
 * the lists behind it have to stay true without someone pressing refresh.
 * Rather than re-render from socket payloads directly, a change invalidates
 * the relevant React Query caches — the socket says *what* changed and the
 * API remains the single source of truth for the data itself.
 */
export function useSignaling(enabled: boolean): SignalingState {
  const qc = useQueryClient();
  const socketRef = useRef<Socket | null>(null);
  const [supportEvent, setSupportEvent] = useState<SupportEvent | null>(null);
  const [connected, setConnected] = useState(false);
  const [presence, setPresence] = useState<Record<string, boolean>>({});
  const [lastSessionChange, setLastSessionChange] = useState<SessionChanged | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const token = tokenStore.getAccessToken();
    if (!token) return;

    const socket = createSignalingSocket(token);
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      void qc.invalidateQueries({ queryKey: qk.support });
    });
    socket.on('support:ready', () => {
      void qc.invalidateQueries({ queryKey: qk.support });
    });
    socket.on('support:changed', (event: SupportEvent) => {
      if (event.change === 'message') setSupportEvent(event);
      void qc.invalidateQueries({ queryKey: qk.support });
    });
    socket.on('disconnect', () => setConnected(false));
    socket.on('connect_error', () => setConnected(false));

    socket.on('presence:update', (event: PresenceUpdate) => {
      setPresence((prev) => ({ ...prev, [event.userId]: event.online }));
      // The online count on the dashboard and the dots in the user table
      // both come from the API, so nudge them rather than patching rows.
      void qc.invalidateQueries({ queryKey: qk.stats });
    });

    socket.on('session:changed', (event: SessionChanged) => {
      setLastSessionChange(event);
      void qc.invalidateQueries({ queryKey: qk.adminSessionsAll });
      void qc.invalidateQueries({ queryKey: qk.mySessionsAll });
      void qc.invalidateQueries({ queryKey: qk.session(event.sessionId) });
      void qc.invalidateQueries({ queryKey: qk.adminSession(event.sessionId) });
      void qc.invalidateQueries({ queryKey: qk.stats });
    });

    // Presence keys expire after 60s on the server; stay well inside that.
    const heartbeat = window.setInterval(() => socket.emit('heartbeat'), 25_000);

    return () => {
      window.clearInterval(heartbeat);
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
      setConnected(false);
    };
  }, [enabled, qc]);

  return { connected, presence, lastSessionChange, supportEvent };
}
